import { createSafeStorage } from "src/stores/safeStorage";

const DISMISSED_USER_QUESTIONS_KEY = "@sudowrite/chat/dismissed-user-questions";
const safeLocalStorage = createSafeStorage(typeof window !== "undefined" ? window.localStorage : undefined);

type DismissedUserQuestionsEntry = {
  messageIds: string[];
  questionSignatures: string[];
};

type LegacyDismissedUserQuestionsCache = Record<string, Record<string, string[]>>;
type DismissedUserQuestionsCache = Record<string, Record<string, DismissedUserQuestionsEntry>>;

function normalizeEntry(entry: unknown): DismissedUserQuestionsEntry {
  if (Array.isArray(entry)) {
    return { messageIds: entry.filter((value): value is string => typeof value === "string"), questionSignatures: [] };
  }
  if (!entry || typeof entry !== "object") {
    return { messageIds: [], questionSignatures: [] };
  }
  const candidate = entry as { messageIds?: unknown; questionSignatures?: unknown };
  const messageIds = Array.isArray(candidate.messageIds)
    ? candidate.messageIds.filter((value): value is string => typeof value === "string")
    : [];
  const questionSignatures = Array.isArray(candidate.questionSignatures)
    ? candidate.questionSignatures.filter((value): value is string => typeof value === "string")
    : [];
  return { messageIds, questionSignatures };
}

function getEntry(
  cache: DismissedUserQuestionsCache,
  projectId: string,
  chatId: string,
): DismissedUserQuestionsEntry {
  return normalizeEntry(cache[projectId]?.[chatId]);
}

function readCache(): DismissedUserQuestionsCache {
  try {
    const raw = safeLocalStorage.getItem(DISMISSED_USER_QUESTIONS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const normalized: DismissedUserQuestionsCache = {};
    for (const [projectId, byChatRaw] of Object.entries(parsed as LegacyDismissedUserQuestionsCache)) {
      if (!byChatRaw || typeof byChatRaw !== "object") continue;
      const byChatNormalized: Record<string, DismissedUserQuestionsEntry> = {};
      for (const [chatId, entry] of Object.entries(byChatRaw)) {
        byChatNormalized[chatId] = normalizeEntry(entry);
      }
      normalized[projectId] = byChatNormalized;
    }
    return normalized;
  } catch {
    return {};
  }
}

function writeCache(cache: DismissedUserQuestionsCache): void {
  try {
    safeLocalStorage.setItem(DISMISSED_USER_QUESTIONS_KEY, JSON.stringify(cache));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export function markDismissedUserQuestion(
  projectId: string | undefined,
  chatId: string | undefined,
  messageId: string | undefined,
): void {
  if (!projectId || !chatId || !messageId) return;
  const cache = readCache();
  const byChat = cache[projectId] ?? {};
  const entry = getEntry(cache, projectId, chatId);
  if (entry.messageIds.includes(messageId)) return;
  byChat[chatId] = { ...entry, messageIds: [...entry.messageIds, messageId] };
  cache[projectId] = byChat;
  writeCache(cache);
}

export function isDismissedUserQuestion(
  projectId: string | undefined,
  chatId: string | undefined,
  messageId: string | undefined,
): boolean {
  if (!projectId || !chatId || !messageId) return false;
  const cache = readCache();
  return getEntry(cache, projectId, chatId).messageIds.includes(messageId);
}

export function markDismissedUserQuestionSignature(
  projectId: string | undefined,
  chatId: string | undefined,
  signature: string | undefined,
): void {
  if (!projectId || !chatId || !signature) return;
  const normalizedSignature = signature.trim();
  if (!normalizedSignature) return;
  const cache = readCache();
  const byChat = cache[projectId] ?? {};
  const entry = getEntry(cache, projectId, chatId);
  if (entry.questionSignatures.includes(normalizedSignature)) return;
  byChat[chatId] = {
    ...entry,
    questionSignatures: [...entry.questionSignatures, normalizedSignature],
  };
  cache[projectId] = byChat;
  writeCache(cache);
}

export function isDismissedUserQuestionSignature(
  projectId: string | undefined,
  chatId: string | undefined,
  signature: string | undefined,
): boolean {
  if (!projectId || !chatId || !signature) return false;
  const normalizedSignature = signature.trim();
  if (!normalizedSignature) return false;
  const cache = readCache();
  return getEntry(cache, projectId, chatId).questionSignatures.includes(normalizedSignature);
}

export function getDismissedUserQuestionMessageIds(
  projectId: string | undefined,
  chatId: string | undefined,
): string[] {
  if (!projectId || !chatId) return [];
  const cache = readCache();
  return getEntry(cache, projectId, chatId).messageIds;
}

/** Clears signature-based dismissals so a resent/edited turn can show the same prompt again. */
export function clearDismissedUserQuestionSignatures(
  projectId: string | undefined,
  chatId: string | undefined,
): void {
  if (!projectId || !chatId) return;
  const cache = readCache();
  const byChat = cache[projectId];
  if (!byChat?.[chatId]) return;
  const entry = getEntry(cache, projectId, chatId);
  if (entry.questionSignatures.length === 0) return;
  byChat[chatId] = { ...entry, questionSignatures: [] };
  cache[projectId] = byChat;
  writeCache(cache);
}
